import { c as _c } from "react/compiler-runtime";
import { SPLASH_TOTAL_MS } from "@/lib/app-launch";
import "./splash.css";

/**
 * The launch splash — the bridge between the native splash and the app.
 *
 * NO `"use client"`, NO HOOKS, NO STATE, and that is the whole design. Every
 * behaviour this surface has lives in `splash.css` (`.app-splash*`), for the
 * two reasons `src/lib/app-launch.ts` sets out at length: it has to PAINT
 * BEFORE HYDRATION, and it has to DISMISS ITSELF EVEN IF NO JAVASCRIPT EVER
 * RUNS. A React-owned overlay fails both — it would cover a page the user had
 * already seen, and a failed bundle would leave it there forever.
 *
 * So this file contributes markup only. It is server-rendered into every
 * document, `display: none` by default, and revealed solely by
 * `[data-app-shell="app"]`. In a browser tab it costs the bytes below and
 * nothing else: no paint, no layout, no effect on LCP, and nothing for a
 * crawler to mistake for an interstitial.
 *
 * WHY THE ARTWORK IS THE ICON'S ARTWORK. The Android TWA paints
 * `manifest.icons` on `background_color` and iOS paints its launch storyboard;
 * both are `#090A0D` with the `N` plate and the NFTBaz wordmark on it (see
 * `src/components/pwa/PwaIconArtwork.tsx`, which generates that icon). This
 * screen opens on the SAME colour with the SAME two elements in the same
 * places, so the native splash appears to come alive rather than cut to a
 * different design. Anything more inventive here would announce the seam.
 *
 * THE WHOLE THING IS `aria-hidden`. It carries no information a user needs —
 * the wordmark is decoration, the meter is indeterminate by design — and a
 * screen reader should be reading the page underneath, which is already there
 * and already complete.
 */

/**
 * The blockchain lattice: a peer graph, not a decorative scatter.
 *
 * Hand-placed rather than generated. A random field reads as noise at this
 * size; these twelve nodes sit in three rough bands with edges only between
 * neighbours, which is what makes it read as a network at a glance — and the
 * gap through the middle is deliberate, so the mark is never fighting an edge
 * for contrast.
 */
const NODES: ReadonlyArray<readonly [x: number, y: number]> = [[60, 72], [150, 40], [250, 88], [322, 52], [34, 180], [120, 166], [232, 196], [300, 158], [70, 272], [165, 300], [255, 282], [330, 240]];
const EDGES: ReadonlyArray<readonly [from: number, to: number]> = [[0, 1], [1, 2], [2, 3], [0, 4], [1, 5], [2, 6], [3, 7], [4, 5], [6, 7], [4, 8], [5, 9], [6, 10], [7, 11], [8, 9], [9, 10], [10, 11]];

/** A pointy-top hexagon, the shape this product frames NFT media with. */
function hexPath(cx: number, cy: number, r: number): string {
  return Array.from({
    length: 6
  }, (_, i) => {
    const angle = Math.PI / 3 * i - Math.PI / 2;
    return `${(cx + r * Math.cos(angle)).toFixed(1)},${(cy + r * Math.sin(angle)).toFixed(1)}`;
  }).join(" ");
}
const HEXES = [{
  cx: 52,
  cy: 330,
  r: 30
}, {
  cx: 318,
  cy: 118,
  r: 38
}, {
  cx: 196,
  cy: 26,
  r: 22
}] as const;
function Lattice() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="app-splash__lattice" viewBox="0 0 360 360" preserveAspectRatio="xMidYMid slice" fill="none" aria-hidden={true} focusable="false">{HEXES.map(_temp)}{EDGES.map(_temp2)}{NODES.map(_temp3)}</svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp3(t0, i_0) {
  const [x, y] = t0;
  return <circle key={`${x}-${y}`} className="app-splash__node" cx={x} cy={y} r={3} fill={i_0 % 3 === 0 ? "#a78bfa" : "#6271eb"} style={{
    animationDelay: `${i_0 % 6 * -400}ms`
  }} />;
}
function _temp2(t0, i) {
  const [from, to] = t0;
  return <line key={`${from}-${to}`} className="app-splash__edge" x1={NODES[from][0]} y1={NODES[from][1]} x2={NODES[to][0]} y2={NODES[to][1]} stroke="rgba(98,113,235,0.5)" strokeWidth={1} style={{
    animationDelay: `${i % 5 * -320}ms`
  }} />;
}
function _temp(hex) {
  return <polygon key={`${hex.cx}-${hex.cy}`} points={hexPath(hex.cx, hex.cy, hex.r)} stroke="rgba(167,139,250,0.28)" strokeWidth={1} />;
}
type AppSplashScreenProps = {
  /** Locale copy. Decorative — the element is `aria-hidden`. */
  tagline: string;
};
export default function AppSplashScreen(t0) {
  const $ = _c(10);
  const {
    tagline
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="app-splash__field" />;
    t2 = <Lattice />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="app-splash__ring" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="app-splash__mark">{t3}<span className="app-splash__plate"><span className="app-splash__glyph">N</span></span></div>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="app-splash__wordmark">NFTBaz</div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== tagline) {
    t6 = <div className="app-splash__titles">{t5}<div className="app-splash__tagline">{tagline}</div></div>;
    $[5] = tagline;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="app-splash__meter" />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6) {
    t8 = <div className="app-splash" aria-hidden={true} role="presentation" data-splash-duration={SPLASH_TOTAL_MS}>{t1}{t2}<div className="app-splash__stage">{t4}{t6}{t7}</div></div>;
    $[8] = t6;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
}